import React, { useCallback, useEffect, useState } from "react";
import { IProductVariant } from "shopify-api-node";
import { EBattleKartGames, TBookingType, TMainTrackRaceTypeFilter } from "../../interfaces";
import { BattleKartGameIcon, getAgeGroupDisplayName, getDatetimeFromSku } from "../../utilities";
import Tooltip from "../Shared/Tooltip";
import Notifier from "../Shared/Notify";

interface VariantsBlockProps {
    variant: IProductVariant;
    isSelected: boolean;
    onClick?: () => void;
    activeRace?: IProductVariant | null;
    track: TBookingType;
    allSelectable?: boolean;
}

// Extract price color logic to a pure function
const getPriceColor = (price: string): string => {
    const priceNumber = parseFloat(price);
    if (priceNumber <= 15) return "bg-[#C03FFF]";
    if (priceNumber <= 19) return "bg-[#C03FFF]";
    if (priceNumber <= 24) return "bg-[#8A75FF]";
    if (priceNumber <= 29) return "bg-[#8A75FF]";
    if (priceNumber <= 39) return "bg-[#C03FFF]";
    if (priceNumber <= 49) return "bg-[#E632E5]";
    if (priceNumber <= 59) return "bg-[#510EA8]";
    if (priceNumber <= 69) return "bg-[#510EA8]";
    return "bg-gray-500";
};

// Common Price Display Component
const PriceDisplay: React.FC<{
    price: string;
    isSelected: boolean;
    isActiveRace: boolean;
    variantId: number;
    className?: string;
}> = ({ price, isSelected, isActiveRace, variantId, className = "" }) => (
    <div
        className={`${getPriceColor(price)} text-[10px] w-100 font-bold text-white mt-auto relative z-[99] w-full md:w-fit px-[4px] mb-[1px] sm:mb-[0px] whitespace-nowrap ${
            isActiveRace && isSelected ? "bg-[#393b4180]" : ""
        } variantId${variantId} ${className}`}
    >
        ${Number(price)}
    </div>
);

// Common Seats Display Component
const SeatsDisplay: React.FC<{
    leftSeats: number;
    track: TBookingType;
    className?: string;
}> = ({ leftSeats, track, className = "" }) => (
    <div className={`text-[10px] pt-[8px] max-[380px]:text-[9px] sm:pt-[0px] md:text-[10.5px] leading-[0] text-white w-full z-[5] ${className}`}>
        <span className="font-bold mr-0.5">{leftSeats || 0}</span> left
    </div>
);

// Common option display component
const OptionDisplay: React.FC<{
    variant: IProductVariant;
    className?: string;
    track: TBookingType;
}> = ({ variant, className = "", track }) => {
    const ageGroupContent = getAgeGroupDisplayName(variant.title)
    const content = ageGroupContent && ["<12", "12-15"].includes(ageGroupContent) ? ageGroupContent : (variant.option2 === TMainTrackRaceTypeFilter.Expert ? "Expert" : null);

    if(track !== "MAIN_TRACK" || !content){
        return <></>;
    }
    
    return (
        <div className={`text-[10px] pt-[8px] max-[380px]:text-[9px] sm:pt-[0px] md:text-[10.5px] leading-[0] text-white w-full z-[5] ${className}`}>
            {content}
        </div>
    );
}

const VariantsBlock: React.FC<VariantsBlockProps> = ({ variant, isSelected, onClick, activeRace, track, allSelectable }) => {
    const isActiveRace = activeRace?.id === variant.id;
    const leftSeats = variant?.inventory_quantity || 0;
    const [warningMessage, setWarningMessage] = useState<string | null>(null);
    const isSoldOut = allSelectable ? false : leftSeats === 0;

    const getTimeForVariant = useCallback((variant: IProductVariant) => {
        switch(track) {
            case "MAIN_TRACK":
            case "JUNIOR_TRACK":
            case "INTERMEDIATE_TRACK":
                return variant.option3 || "";
            case "MINI_TRACK":
            case "VR":
            case "BATTLE_KART":
                return variant.option1 || "";
            default:
                const dt = getDatetimeFromSku(variant.sku);
                return dt ? dt.toFormat("HH:mm a") : "";
        }
    }, [track]);

    const handleClick = () => {
        if (onClick && !isSoldOut) {
            onClick();
        }
    };

    useEffect(() => {
        if (!warningMessage) return;
        
        const timer = setTimeout(() => setWarningMessage(null), 3000);
        return () => clearTimeout(timer);
    }, [warningMessage]);

    const timeDisplay = getTimeForVariant(variant);

    // Battle Kart Layout
    if (track === "BATTLE_KART") {
        return (
            <div
                onClick={handleClick}
                className={`rounded-[6px] overflow-hidden cursor-pointer flex flex-col relative z-[39] m-[2px] font-medium min-h-[50px] ${
                    isSoldOut ? "opacity-50" : ""
                }`}
            >
                {/* Top section with time and game modes */}
                <div className={`flex flex-row h-[32px] ${isSelected ? "bg-gradient-to-r from-[#EA581A] to-[#FAC618]" : "bg-white"}`}>
                    {/* Time section - moved to left side */}
                    <div className={`w-[55%] flex items-center justify-center`}>
                        <div className={`text-[16px] leading-tight font-medium ${isSelected ? "text-white" : "text-black"}`}>
                            {timeDisplay}
                        </div>
                    </div>

                    {/* Game modes section - moved to right side, vertically centered */}
                    <div className={`w-[45%] flex items-center justify-center`}>
                        {variant.option2 && Object.keys(BattleKartGameIcon).includes(variant.option2) && (
                            <div className="w-[50%] h-[22px] rounded-[4px] overflow-hidden">
                                <img src={BattleKartGameIcon[variant.option2 as EBattleKartGames]} alt={variant.option2} width={22}/>
                            </div>
                        )}
                        {variant.option3 && Object.keys(BattleKartGameIcon).includes(variant.option3) && (
                            <div className="h-[22px] rounded-[4px] overflow-hidden">
                                <img src={BattleKartGameIcon[variant.option3 as EBattleKartGames]} alt={variant.option3} width={22}/>
                            </div>
                        )}
                    </div>
                </div>

                {/* Price and seats section */}
                <div className="flex h-[18px]">
                    <div className={`${getPriceColor(variant.price)} w-[30%] py-[1px] px-[1px] text-center flex items-center justify-center`}>
                        <div className="text-white text-[12px] font-bold">${Number(variant.price)}</div>
                    </div>
                    <div className="bg-black w-[70%] py-[1px] px-[1px] text-left flex items-center justify-start pl-[8px]">
                        <div className="text-white text-[10px] md:text-[11px] font-bold">{leftSeats} left</div>
                    </div>
                </div>
            </div>
        );
    }

    // Sold Out Layout
    if (isSoldOut) {
        return (
            <Tooltip tooltipText="Seats required exceeds available">
                {warningMessage && <Notifier message={warningMessage} />}
                <div
                    className="bg-[#c8c8c8] rounded-[5px] overflow-hidden text-center cursor-pointer flex flex-col justify-center relative z-[39] m-[2px] font-medium"
                    onMouseEnter={() => setWarningMessage("Seats required exceeds available")}
                    onClick={() => setWarningMessage("Seats required exceeds available")}
                >
                    <div className="text-[#494949] text-[18px] time relative z-[99]">
                        {timeDisplay}
                    </div>
                    <div className="flex flex-col sm:flex-row md:flex-row pb-[8px] sm:pb-[0px] items-center bg-black">
                        <div className="text-[12px] w-100 font-bold bg-[#7e7e7e] text-[#b4b4b4] mt-auto relative z-[99] w-full md:w-fit px-[4px] mb-[1px] sm:mb-[0px] whitespace-nowrap">
                            ${Number(variant.price)}
                        </div>
                        <div className="text-[10px] pt-[8px] sm:pt-[0px] md:text-[12px] leading-[0] text-[#b4b4b4] w-full z-[5]">
                            <span className="font-bold mr-0.5">{leftSeats}</span> left
                        </div>
                    </div>
                </div>
            </Tooltip>
        );
    }

    // Regular Available Layout
    return (
        <div
            onClick={handleClick}
            className={`rounded-[5px] overflow-hidden text-center cursor-pointer flex flex-col justify-center relative z-[39] m-[2px] font-medium ${
                isSelected ? "text-white border-color" : "bg-white justify-between min-h-[44px]"
            }`}
        >
            <div className={`text-[15px] md:text-[18px] time relative z-[99] ${
                isSelected ? "text-white border-color" : "text-[#393B41]"
            }`}>
                {timeDisplay}
            </div>
            
            <div className="flex flex-col sm:flex-row pb-[8px] sm:pb-[0px] items-center bg-black">
                <PriceDisplay
                    price={variant.price}
                    isSelected={isSelected}
                    isActiveRace={isActiveRace}
                    variantId={variant.id}
                    className="w-full sm:w-auto"
                />
                <SeatsDisplay
                    leftSeats={leftSeats}
                    track={track}
                />
                <OptionDisplay 
                    variant={variant} 
                    track={track}
                />
            </div>
        </div>
    );
};

export default VariantsBlock;